import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Building2, 
  User, 
  Settings, 
  Eye, 
  CheckCircle, 
  AlertCircle,
  ArrowLeft,
  Users,
  Tag,
  Flag,
  Shield
} from 'lucide-react';
import Modal from '../components/ui/Modal';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/Separator';
import { Tabs } from '../components/ui/Tabs';
import { TabsContent } from '../components/ui/Tabs';
import { TabsList } from '../components/ui/Tabs';
import { TabsTrigger } from '../components/ui/Tabs';
import PhoneInput from '../components/forms/PhoneInput';
import { toast } from 'react-hot-toast';
import apiService from '../services/api';

interface CompanyType {
  value: number;
  label: string;
  description: string;
}

interface SeedDataPreview {
  companyType: number;
  baseCategories: Array<{ name: string; description: string }>;
  basePriorities: Array<{ name: string; description: string; sortOrder: number }>;
  baseRoles: Array<{ name: string; description: string }>;
  baseStatuses: Array<{ name: string; description: string; sortOrder: number; tabGroup: string }>;
  industryCategories: Array<{ name: string; description: string }>;
  industryPriorities: Array<{ name: string; description: string; sortOrder: number }>;
  industryRoles: Array<{ name: string; description: string }>;
  industryStatuses: Array<{ name: string; description: string; sortOrder: number; tabGroup: string }>;
}

interface CreateCompanyForm {
  // Company Information
  name: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  phone: string;
  email: string;
  website: string;

  // Company Type & Seeding
  companyType: number;
  preSeedData: boolean;

  // Subscription Plan
  subscriptionPlanId: string;
  startWithTrial: boolean;

  // Admin User
  adminFirstName: string;
  adminLastName: string;
  adminEmail: string;
  adminPhone: string;
  forcePasswordChange: boolean;
  sendWelcomeEmail: boolean;
}

interface FormErrors {
  [key: string]: string;
}

const companyTypes: CompanyType[] = [
  { value: 7, label: 'Churches', description: 'Religious organizations, churches, temples' },
  { value: 8, label: 'Custom', description: 'No pre-seeding, fully customizable setup' },
  { value: 2, label: 'Educational Institutions', description: 'Schools, universities, training centers' },
  { value: 0, label: 'Facilities Management', description: 'General facilities management' },
  { value: 5, label: 'Government/Municipal', description: 'Municipalities, public services, agencies' },
  { value: 1, label: 'Healthcare Facilities', description: 'Hospitals, clinics, medical centers' },
  { value: 6, label: 'Hospitality/Events', description: 'Hotels, resorts, event venues' },
  { value: 4, label: 'Manufacturing/Warehouse', description: 'Factories, production plants, warehouses' },
  { value: 9, label: 'Property Management', description: 'Apartments, condos, multi-family residential' },
  { value: 3, label: 'Retail/Restaurant', description: 'Stores, shopping centers, restaurants' }
];

const CreateCompanyPage: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<CreateCompanyForm>({
    name: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    phone: '',
    email: '',
    website: '',
    companyType: -1,
    preSeedData: true,
    subscriptionPlanId: 'professional',
    startWithTrial: true,
    adminFirstName: '',
    adminLastName: '',
    adminEmail: '',
    adminPhone: '',
    forcePasswordChange: true,
    sendWelcomeEmail: true
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [showSeedPreview, setShowSeedPreview] = useState(false);
  const [subscriptionPlans, setSubscriptionPlans] = useState<any[]>([]);
  const [seedPreview, setSeedPreview] = useState<SeedDataPreview | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [createdCompany, setCreatedCompany] = useState<any>(null);

  // Generate temporary password
  const generateTempPassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  };

  const handleInputChange = (field: keyof CreateCompanyForm, value: string | boolean | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Company Information validation
    if (!formData.name.trim()) newErrors.name = 'Company name is required';
    if (!formData.adminFirstName.trim()) newErrors.adminFirstName = 'Admin first name is required';
    if (!formData.adminLastName.trim()) newErrors.adminLastName = 'Admin last name is required';
    if (!formData.adminEmail.trim()) newErrors.adminEmail = 'Admin email is required';
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    if (formData.adminEmail && !emailRegex.test(formData.adminEmail)) {
      newErrors.adminEmail = 'Invalid email format';
    }

    // Phone validation (if provided)
    const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
    if (formData.phone && !phoneRegex.test(formData.phone.replace(/[\s\-\(\)]/g, ''))) {
      newErrors.phone = 'Invalid phone number format';
    }
    if (formData.adminPhone && !phoneRegex.test(formData.adminPhone.replace(/[\s\-\(\)]/g, ''))) {
      newErrors.adminPhone = 'Invalid phone number format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const loadSeedPreview = async () => {
    if (formData.companyType === 8) return; // Custom type has no preview
    
    setIsLoadingPreview(true);
    try {
      const preview = await apiService.getSeedDataPreview(formData.companyType);
      setSeedPreview(preview);
      setShowSeedPreview(true);
    } catch (error) {
      toast.error('Failed to load seed data preview');
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fix the errors before submitting');
      return;
    }

    setIsLoading(true);
    try {
      const tempPassword = generateTempPassword();
      
      const payload = {
        name: formData.name,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        zipCode: formData.zipCode,
        phone: formData.phone,
        email: formData.email,
        website: formData.website,
        companyType: formData.companyType,
        preSeedData: formData.preSeedData,
        subscriptionPlanId: formData.subscriptionPlanId,
        startWithTrial: formData.startWithTrial,
        adminFirstName: formData.adminFirstName,
        adminLastName: formData.adminLastName,
        adminEmail: formData.adminEmail,
        adminPhone: formData.adminPhone,
        adminPassword: tempPassword,
        forcePasswordChange: formData.forcePasswordChange,
        sendWelcomeEmail: formData.sendWelcomeEmail
      };

      const result = await apiService.createCompanyWithSeeding(payload);
      
      setCreatedCompany({
        ...result,
        tempPassword
      });
      setShowSuccess(true);
      
      toast.success('Company created successfully!');
    } catch (error: any) {
      console.error('Error creating company:', error);
      
      // Extract error message from response
      const errorMessage = error.response?.data?.message || error.message || 'Failed to create company. Please try again.';
      const errorData = error.response?.data;
      
      // Check for duplicate email specific errors
      const isEmailError = errorMessage.toLowerCase().includes('email') && 
                          (errorMessage.toLowerCase().includes('already') ||
                           errorMessage.toLowerCase().includes('duplicate') ||
                           errorMessage.toLowerCase().includes('unique') ||
                           errorMessage.toLowerCase().includes('registered'));
      
      if (isEmailError) {
        // Set field-specific error for adminEmail
        setErrors(prev => ({
          ...prev,
          adminEmail: 'This email address is already registered. Please use a different email address.'
        }));
        toast.error('This email address is already registered. Each user must have a unique email address across all companies.');
      } else if (errorData?.errors && Array.isArray(errorData.errors) && errorData.errors.length > 0) {
        // Handle field-specific errors from backend
        const emailError = errorData.errors.find((err: string) => 
          err.toLowerCase().includes('email') || err.toLowerCase().includes('already')
        );
        if (emailError) {
          setErrors(prev => ({
            ...prev,
            adminEmail: emailError
          }));
        }
        toast.error(errorMessage);
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuccessClose = () => {
    setShowSuccess(false);
    navigate('/systemadmin');
  };

  const handleCreateAnother = () => {
    setShowSuccess(false);
    setFormData({
      name: '',
      address: '',
      city: '',
      state: '',
      zipCode: '',
      phone: '',
      email: '',
      website: '',
      companyType: 0,
      preSeedData: true,
      adminFirstName: '',
      adminLastName: '',
      adminEmail: '',
      adminPhone: '',
      forcePasswordChange: true,
      sendWelcomeEmail: true
    });
    setErrors({});
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="container mx-auto px-4 py-8">
        {/* Breadcrumb */}
        <nav className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400 mb-6">
          <button 
            onClick={() => navigate('/superadmin/companies')}
            className="hover:text-gray-900 dark:hover:text-gray-100"
          >
            SuperAdmin
          </button>
          <span>/</span>
          <button 
            onClick={() => navigate('/superadmin/companies')}
            className="hover:text-gray-900 dark:hover:text-gray-100"
          >
            Companies
          </button>
          <span>/</span>
          <span className="text-gray-900 dark:text-gray-100">Create New Company</span>
        </nav>

        {/* Header */}
        <div className="flex items-center space-x-3 mb-8">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/superadmin/companies')}
            className="flex items-center space-x-2"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Back</span>
          </Button>
          <div className="flex items-center space-x-3">
            <Building2 className="h-8 w-8 text-accent-color" />
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                Create New Company
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                Set up a new company with industry-specific data and admin user
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Company Information Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Building2 className="h-5 w-5" />
                <span>Company Information</span>
              </CardTitle>
              <CardDescription>
                Basic company details and contact information
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <Label htmlFor="name">Company Name <span className="text-red-500">*</span></Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="Enter company name"
                    className={errors.name ? 'border-red-500' : ''}
                  />
                  {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                </div>

                <div>
                  <Label htmlFor="email">Company Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="company@example.com"
                    className={errors.email ? 'border-red-500' : ''}
                  />
                  {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                </div>

                <div>
                  <Label htmlFor="phone">Company Phone</Label>
                  <PhoneInput
                    value={formData.phone}
                    onChange={(value) => handleInputChange('phone', value)}
                    placeholder="(555) 123-4567"
                    className={errors.phone ? 'border-red-500' : ''}
                  />
                  {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
                </div>

                <div>
                  <Label htmlFor="website">Website</Label>
                  <Input
                    id="website"
                    value={formData.website}
                    onChange={(e) => handleInputChange('website', e.target.value)}
                    placeholder="https://www.example.com"
                  />
                </div>

              </div>

              <div>
                <Label htmlFor="address">Address</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  placeholder="123 Main Street"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                    placeholder="New York"
                  />
                </div>

                <div>
                  <Label htmlFor="state">State</Label>
                  <Select 
                    value={formData.state} 
                    onValueChange={(value) => handleInputChange('state', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="AL">Alabama (AL)</SelectItem>
                      <SelectItem value="AK">Alaska (AK)</SelectItem>
                      <SelectItem value="AZ">Arizona (AZ)</SelectItem>
                      <SelectItem value="AR">Arkansas (AR)</SelectItem>
                      <SelectItem value="CA">California (CA)</SelectItem>
                      <SelectItem value="CO">Colorado (CO)</SelectItem>
                      <SelectItem value="CT">Connecticut (CT)</SelectItem>
                      <SelectItem value="DE">Delaware (DE)</SelectItem>
                      <SelectItem value="FL">Florida (FL)</SelectItem>
                      <SelectItem value="GA">Georgia (GA)</SelectItem>
                      <SelectItem value="HI">Hawaii (HI)</SelectItem>
                      <SelectItem value="ID">Idaho (ID)</SelectItem>
                      <SelectItem value="IL">Illinois (IL)</SelectItem>
                      <SelectItem value="IN">Indiana (IN)</SelectItem>
                      <SelectItem value="IA">Iowa (IA)</SelectItem>
                      <SelectItem value="KS">Kansas (KS)</SelectItem>
                      <SelectItem value="KY">Kentucky (KY)</SelectItem>
                      <SelectItem value="LA">Louisiana (LA)</SelectItem>
                      <SelectItem value="ME">Maine (ME)</SelectItem>
                      <SelectItem value="MD">Maryland (MD)</SelectItem>
                      <SelectItem value="MA">Massachusetts (MA)</SelectItem>
                      <SelectItem value="MI">Michigan (MI)</SelectItem>
                      <SelectItem value="MN">Minnesota (MN)</SelectItem>
                      <SelectItem value="MS">Mississippi (MS)</SelectItem>
                      <SelectItem value="MO">Missouri (MO)</SelectItem>
                      <SelectItem value="MT">Montana (MT)</SelectItem>
                      <SelectItem value="NE">Nebraska (NE)</SelectItem>
                      <SelectItem value="NV">Nevada (NV)</SelectItem>
                      <SelectItem value="NH">New Hampshire (NH)</SelectItem>
                      <SelectItem value="NJ">New Jersey (NJ)</SelectItem>
                      <SelectItem value="NM">New Mexico (NM)</SelectItem>
                      <SelectItem value="NY">New York (NY)</SelectItem>
                      <SelectItem value="NC">North Carolina (NC)</SelectItem>
                      <SelectItem value="ND">North Dakota (ND)</SelectItem>
                      <SelectItem value="OH">Ohio (OH)</SelectItem>
                      <SelectItem value="OK">Oklahoma (OK)</SelectItem>
                      <SelectItem value="OR">Oregon (OR)</SelectItem>
                      <SelectItem value="PA">Pennsylvania (PA)</SelectItem>
                      <SelectItem value="RI">Rhode Island (RI)</SelectItem>
                      <SelectItem value="SC">South Carolina (SC)</SelectItem>
                      <SelectItem value="SD">South Dakota (SD)</SelectItem>
                      <SelectItem value="TN">Tennessee (TN)</SelectItem>
                      <SelectItem value="TX">Texas (TX)</SelectItem>
                      <SelectItem value="UT">Utah (UT)</SelectItem>
                      <SelectItem value="VT">Vermont (VT)</SelectItem>
                      <SelectItem value="VA">Virginia (VA)</SelectItem>
                      <SelectItem value="WA">Washington (WA)</SelectItem>
                      <SelectItem value="WV">West Virginia (WV)</SelectItem>
                      <SelectItem value="WI">Wisconsin (WI)</SelectItem>
                      <SelectItem value="WY">Wyoming (WY)</SelectItem>
                      <SelectItem value="DC">District of Columbia (DC)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="zipCode">ZIP Code</Label>
                  <Input
                    id="zipCode"
                    value={formData.zipCode}
                    onChange={(e) => handleInputChange('zipCode', e.target.value)}
                    placeholder="10001"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Company Type & Seeding Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Settings className="h-5 w-5" />
                <span>Company Type & Data Seeding</span>
              </CardTitle>
              <CardDescription>
                Select the industry type and configure initial data setup
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label htmlFor="companyType">Company Type *</Label>
                <select
                  id="companyType"
                  value={formData.companyType.toString()}
                  onChange={(e) => handleInputChange('companyType', parseInt(e.target.value))}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-accent-color focus:border-accent-color bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  <option value="-1">-- Please Select Company Type --</option>
                  {companyTypes.map((type) => (
                    <option key={type.value} value={type.value.toString()}>
                      {type.label} - {type.description}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="preSeedData"
                  checked={formData.preSeedData}
                  onCheckedChange={(checked) => handleInputChange('preSeedData', checked as boolean)}
                />
                <Label htmlFor="preSeedData" className="text-sm">
                  Pre-seed company data with industry-specific categories, priorities, roles, and statuses
                </Label>
              </div>

              {formData.preSeedData && formData.companyType !== 8 && (
                <div className="flex items-center space-x-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={loadSeedPreview}
                    disabled={isLoadingPreview}
                    className="flex items-center space-x-2"
                  >
                    <Eye className="h-4 w-4" />
                    <span>{isLoadingPreview ? 'Loading...' : 'Preview Seed Data'}</span>
                  </Button>
                  <span className="text-sm text-gray-500">
                    See what data will be created for this company type
                  </span>
                </div>
              )}

              {formData.companyType === 8 && (
                <div className="bg-accent-color-50 dark:bg-accent-color-900/20 border border-accent-color-200 dark:border-accent-color-800 rounded-lg p-4">
                  <div className="flex items-center space-x-2">
                    <AlertCircle className="h-5 w-5 text-accent-color" />
                    <span className="text-sm font-medium text-accent-color-800 dark:text-accent-color-200">
                      Custom Setup
                    </span>
                  </div>
                  <p className="text-sm text-accent-color-700 dark:text-accent-color-300 mt-1">
                    No pre-seeding will occur. You can manually configure categories, priorities, roles, and statuses after company creation.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Subscription Plan Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <CheckCircle className="h-5 w-5" />
                <span>Subscription Plan</span>
              </CardTitle>
              <CardDescription>
                Select the subscription plan for this company
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label htmlFor="subscriptionPlanId">Plan *</Label>
                <select
                  id="subscriptionPlanId"
                  value={formData.subscriptionPlanId}
                  onChange={(e) => handleInputChange('subscriptionPlanId', e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-accent-color focus:border-accent-color bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  <option value="starter">Starter - $0/month (3 users, 25 WOs/month, no invoicing)</option>
                  <option value="professional">Professional - $79/month (10 users, unlimited WOs, full invoicing)</option>
                  <option value="enterprise">Enterprise - $299/month (Unlimited users, API access)</option>
                </select>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="startWithTrial"
                  checked={formData.startWithTrial}
                  onCheckedChange={(checked) => handleInputChange('startWithTrial', checked as boolean)}
                />
                <Label htmlFor="startWithTrial" className="text-sm">
                  Start with 14-day free trial of {formData.subscriptionPlanId === 'starter' ? 'Professional' : formData.subscriptionPlanId.charAt(0).toUpperCase() + formData.subscriptionPlanId.slice(1)} features
                </Label>
              </div>

              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <div className="flex items-start space-x-2">
                  <AlertCircle className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
                      Trial Information
                    </p>
                    <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                      {formData.startWithTrial
                        ? 'The company will start with a 14-day trial period with full Professional features, or until they create 10 work orders (whichever comes first).'
                        : `The company will be immediately activated with the ${formData.subscriptionPlanId.charAt(0).toUpperCase() + formData.subscriptionPlanId.slice(1)} plan.`
                      }
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Admin User Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <User className="h-5 w-5" />
                <span>Admin User Setup</span>
              </CardTitle>
              <CardDescription>
                Create the initial administrator user for this company
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="adminFirstName">First Name <span className="text-red-500">*</span></Label>
                  <Input
                    id="adminFirstName"
                    value={formData.adminFirstName}
                    onChange={(e) => handleInputChange('adminFirstName', e.target.value)}
                    placeholder="John"
                    className={errors.adminFirstName ? 'border-red-500' : ''}
                  />
                  {errors.adminFirstName && <p className="text-red-500 text-sm mt-1">{errors.adminFirstName}</p>}
                </div>

                <div>
                  <Label htmlFor="adminLastName">Last Name <span className="text-red-500">*</span></Label>
                  <Input
                    id="adminLastName"
                    value={formData.adminLastName}
                    onChange={(e) => handleInputChange('adminLastName', e.target.value)}
                    placeholder="Doe"
                    className={errors.adminLastName ? 'border-red-500' : ''}
                  />
                  {errors.adminLastName && <p className="text-red-500 text-sm mt-1">{errors.adminLastName}</p>}
                </div>

                <div>
                  <Label htmlFor="adminEmail">Email Address <span className="text-red-500">*</span></Label>
                  <Input
                    id="adminEmail"
                    type="email"
                    value={formData.adminEmail}
                    onChange={(e) => handleInputChange('adminEmail', e.target.value)}
                    placeholder="admin@company.com"
                    className={errors.adminEmail ? 'border-red-500' : ''}
                  />
                  {errors.adminEmail && <p className="text-red-500 text-sm mt-1">{errors.adminEmail}</p>}
                </div>

                <div>
                  <Label htmlFor="adminPhone">Phone Number</Label>
                  <PhoneInput
                    value={formData.adminPhone}
                    onChange={(value) => handleInputChange('adminPhone', value)}
                    placeholder="(555) 123-4567"
                    className={errors.adminPhone ? 'border-red-500' : ''}
                  />
                  {errors.adminPhone && <p className="text-red-500 text-sm mt-1">{errors.adminPhone}</p>}
                </div>
              </div>

              <Separator />

              <div className="space-y-4">
                <h4 className="font-medium text-gray-900 dark:text-gray-100">Account Settings</h4>
                
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="forcePasswordChange"
                    checked={formData.forcePasswordChange}
                    onCheckedChange={(checked) => handleInputChange('forcePasswordChange', checked as boolean)}
                  />
                  <Label htmlFor="forcePasswordChange" className="text-sm">
                    Force password change on first login
                  </Label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="sendWelcomeEmail"
                    checked={formData.sendWelcomeEmail}
                    onCheckedChange={(checked) => handleInputChange('sendWelcomeEmail', checked as boolean)}
                  />
                  <Label htmlFor="sendWelcomeEmail" className="text-sm">
                    Send welcome email with login credentials
                  </Label>
                </div>

                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                  <div className="flex items-center space-x-2">
                    <AlertCircle className="h-5 w-5 text-yellow-600" />
                    <span className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                      Temporary Password
                    </span>
                  </div>
                  <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                    A secure temporary password will be generated automatically. The admin user will be required to change it on first login.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Submit Button */}
          <div className="flex justify-end space-x-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/superadmin/companies')}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
              className="flex items-center space-x-2"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Creating Company...</span>
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4" />
                  <span>Create Company</span>
                </>
              )}
            </Button>
          </div>
        </form>

        {/* Seed Data Preview Modal */}
        {showSeedPreview && seedPreview && (
          <Modal
            isOpen={showSeedPreview}
            onClose={() => setShowSeedPreview(false)}
            title="Seed Data Preview"
            maxWidth="2xl"
          >
            <div className="space-y-6">
              <div className="bg-accent-color-50 dark:bg-accent-color-900/20 border border-accent-color-200 dark:border-accent-color-800 rounded-lg p-4">
                <div className="flex items-center space-x-2">
                  <Eye className="h-5 w-5 text-accent-color" />
                  <span className="text-sm font-medium text-accent-color-800 dark:text-accent-color-200">
                    Preview Only
                  </span>
                </div>
                <p className="text-sm text-accent-color-700 dark:text-accent-color-300 mt-1">
                  This shows what data will be created. You can customize categories, priorities, roles, and statuses after company creation.
                </p>
              </div>

              <Tabs defaultValue="categories" className="w-full">
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="categories" className="flex items-center space-x-2">
                    <Tag className="h-4 w-4" />
                    <span>Categories</span>
                  </TabsTrigger>
                  <TabsTrigger value="priorities" className="flex items-center space-x-2">
                    <Flag className="h-4 w-4" />
                    <span>Priorities</span>
                  </TabsTrigger>
                  <TabsTrigger value="roles" className="flex items-center space-x-2">
                    <Users className="h-4 w-4" />
                    <span>Roles</span>
                  </TabsTrigger>
                  <TabsTrigger value="statuses" className="flex items-center space-x-2">
                    <Shield className="h-4 w-4" />
                    <span>Statuses</span>
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="categories" className="space-y-4">
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3">Base Categories</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {seedPreview.baseCategories.map((category, index) => (
                        <div key={index} className="flex items-center space-x-2 p-2 bg-gray-50 dark:bg-gray-800 rounded">
                          <Tag className="h-4 w-4 text-gray-500" />
                          <div>
                            <span className="text-sm font-medium">{category.name}</span>
                            <p className="text-xs text-gray-500">{category.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {seedPreview.industryCategories.length > 0 && (
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3">
                        Industry-Specific Categories
                        <Badge variant="secondary" className="ml-2">+{seedPreview.industryCategories.length}</Badge>
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {seedPreview.industryCategories.map((category, index) => (
                          <div key={index} className="flex items-center space-x-2 p-2 bg-accent-color-50 dark:bg-accent-color-900/20 rounded">
                            <Tag className="h-4 w-4 text-accent-color" />
                            <div>
                              <span className="text-sm font-medium">{category.name}</span>
                              <p className="text-xs text-gray-500">{category.description}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="priorities" className="space-y-4">
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3">Base Priorities</h4>
                    <div className="space-y-2">
                      {seedPreview.basePriorities.map((priority, index) => (
                        <div key={index} className="flex items-center space-x-2 p-2 bg-gray-50 dark:bg-gray-800 rounded">
                          <Flag className="h-4 w-4 text-gray-500" />
                          <div>
                            <span className="text-sm font-medium">{priority.name}</span>
                            <p className="text-xs text-gray-500">{priority.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {seedPreview.industryPriorities.length > 0 && (
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3">
                        Industry-Specific Priorities
                        <Badge variant="secondary" className="ml-2">+{seedPreview.industryPriorities.length}</Badge>
                      </h4>
                      <div className="space-y-2">
                        {seedPreview.industryPriorities.map((priority, index) => (
                          <div key={index} className="flex items-center space-x-2 p-2 bg-accent-color-50 dark:bg-accent-color-900/20 rounded">
                            <Flag className="h-4 w-4 text-accent-color" />
                            <div>
                              <span className="text-sm font-medium">{priority.name}</span>
                              <p className="text-xs text-gray-500">{priority.description}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="roles" className="space-y-4">
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3">Base Roles</h4>
                    <div className="space-y-2">
                      {seedPreview.baseRoles.map((role, index) => (
                        <div key={index} className="flex items-center space-x-2 p-2 bg-gray-50 dark:bg-gray-800 rounded">
                          <Users className="h-4 w-4 text-gray-500" />
                          <div>
                            <span className="text-sm font-medium">{role.name}</span>
                            <p className="text-xs text-gray-500">{role.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {seedPreview.industryRoles.length > 0 && (
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3">
                        Industry-Specific Roles
                        <Badge variant="secondary" className="ml-2">+{seedPreview.industryRoles.length}</Badge>
                      </h4>
                      <div className="space-y-2">
                        {seedPreview.industryRoles.map((role, index) => (
                          <div key={index} className="flex items-center space-x-2 p-2 bg-accent-color-50 dark:bg-accent-color-900/20 rounded">
                            <Users className="h-4 w-4 text-accent-color" />
                            <div>
                              <span className="text-sm font-medium">{role.name}</span>
                              <p className="text-xs text-gray-500">{role.description}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="statuses" className="space-y-4">
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3">Base Statuses</h4>
                    <div className="space-y-2">
                      {seedPreview.baseStatuses?.map((status, index) => (
                        <div key={index} className="flex items-start space-x-2 p-2 bg-gray-50 dark:bg-gray-800 rounded">
                          <Shield className="h-4 w-4 text-gray-500 mt-0.5" />
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium">{status.name}</span>
                              <Badge variant="outline" className="text-xs">{status.tabGroup}</Badge>
                            </div>
                            <p className="text-xs text-gray-500">{status.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {seedPreview.industryStatuses && seedPreview.industryStatuses.length > 0 && (
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3">
                        Industry-Specific Statuses
                        <Badge variant="secondary" className="ml-2">+{seedPreview.industryStatuses.length}</Badge>
                      </h4>
                      <div className="space-y-2">
                        {seedPreview.industryStatuses.map((status, index) => (
                          <div key={index} className="flex items-start space-x-2 p-2 bg-accent-color-50 dark:bg-accent-color-900/20 rounded">
                            <Shield className="h-4 w-4 text-accent-color mt-0.5" />
                            <div className="flex-1">
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-medium">{status.name}</span>
                                <Badge variant="outline" className="text-xs">{status.tabGroup}</Badge>
                              </div>
                              <p className="text-xs text-gray-500">{status.description}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </div>
          </Modal>
        )}

        {/* Success Modal */}
        {showSuccess && createdCompany && (
          <Modal
            isOpen={showSuccess}
            onClose={handleSuccessClose}
            title="Company Created Successfully!"
            maxWidth="2xl"
          >
            <div className="space-y-6">
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <span className="text-sm font-medium text-green-800 dark:text-green-200">
                    Setup Complete
                  </span>
                </div>
                <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                  The company has been created successfully with all requested configurations.
                </p>
              </div>

              <div className="space-y-4">
                <h4 className="font-medium text-gray-900 dark:text-gray-100">Company Details</h4>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Company Name:</span>
                    <span className="text-sm font-medium">
                      {createdCompany.company?.name || 'N/A'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Company Type:</span>
                    <span className="text-sm font-medium">
                      {(() => {
                        const typeValue = createdCompany.company?.companyType ?? createdCompany.companyType;
                        return companyTypes.find(t => t.value === Number(typeValue))?.label || 'N/A';
                      })()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Data Seeded:</span>
                    <span className="text-sm font-medium">
                      {createdCompany.preSeedData ? 'Yes' : 'No'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h4 className="font-medium text-gray-900 dark:text-gray-100">Admin User Credentials</h4>
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Email:</span>
                      <span className="text-sm font-medium">
                        {createdCompany.adminUser?.email || 'N/A'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Temporary Password:</span>
                      <span className="text-sm font-mono bg-white dark:bg-gray-800 px-2 py-1 rounded border">
                        {createdCompany.tempPassword}
                      </span>
                    </div>
                  </div>
                  <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded">
                    <div className="flex items-center space-x-2">
                      <AlertCircle className="h-4 w-4 text-red-600" />
                      <span className="text-sm font-medium text-red-800 dark:text-red-200">
                        Important Security Notice
                      </span>
                    </div>
                    <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                      Please securely share these credentials with the admin user. The password must be changed on first login.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h4 className="font-medium text-gray-900 dark:text-gray-100">Next Steps</h4>
                <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                  <p>• Share admin credentials securely with the company administrator</p>
                  <p>• The admin user should log in and change their password immediately</p>
                  <p>• Review and customize seeded data as needed for the company</p>
                  <p>• Set up additional users and configure company-specific settings</p>
                </div>
              </div>

              <div className="flex justify-end space-x-4 pt-4">
                <Button
                  variant="outline"
                  onClick={handleCreateAnother}
                >
                  Create Another Company
                </Button>
                <Button onClick={handleSuccessClose}>
                  Return to Companies List
                </Button>
              </div>
            </div>
          </Modal>
        )}
      </div>
    </div>
  );
};

export default CreateCompanyPage;
